import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Alert, Button, Card, Descriptions, Drawer, Empty, Form, Input, InputNumber, Modal, Popconfirm, Radio, Result, Select, Space, Switch, Table, Tabs, Tag,
    Typography, message,
} from 'antd';
import { ReloadOutlined, SyncOutlined } from '@ant-design/icons';
import apiClient from '../../../shared/services/apiClient';
import { adminPath } from '../../../shared/config/paths';

const { Text } = Typography;
const API = '/api/admin/sites';

const ok = (r) => r?.data?.success !== false;
const data = (r) => (r?.data && 'data' in r.data ? r.data.data : r?.data);
const err = (e, fallback = '失败') => { if (!e?.toasted) message.error(e?.response?.data?.error || e?.response?.data?.message || e?.message || fallback); };
const listOf = (d) => (Array.isArray(d) ? d : Array.isArray(d?.items) ? d.items : []);
const bytes = (b) => {
    const n = Number(b) || 0;
    if (n < 1024) return `${n} B`;
    const u = ['KB', 'MB', 'GB', 'TB']; let v = n / 1024; let i = 0;
    while (v >= 1024 && i < u.length - 1) { v /= 1024; i += 1; }
    return `${v.toFixed(v < 10 ? 1 : 0)} ${u[i]}`;
};
const storageText = (used, limit) => `${bytes(used)} / ${Number(limit) < 0 ? '不限' : limit ? bytes(limit) : '-'}`;
const fmt = (v) => (v ? String(v).replace('T', ' ').replace(/\.\d+Z?$|Z$/, '') : '-');

const STATUS = { draft: ['default', '草稿'], active: ['green', '已发布'], suspended: ['red', '已暂停'], deleted: ['default', '已删除'] };
const StatusTag = ({ s }) => { const [c, l] = STATUS[s] || ['default', s]; return <Tag color={c}>{l}</Tag>; };
const DOMAIN_STATUS = { active: ['green', '生效'], pending: ['gold', '待验证'], error: ['red', '错误'] };

/** 建站(AI site builder)管理:站点 / 组件库 / 收款设置(Stripe Connect)。运维见 api/docs/SITES.md */
const SiteDetail = ({ id, onClose, onChanged }) => {
    const [d, setD] = useState(null);
    const [busy, setBusy] = useState(false);
    const load = useCallback(() => {
        setD(null);
        apiClient.get(`${API}/${id}`).then((r) => setD(data(r))).catch((e) => { err(e, '加载失败'); onClose(); });
    }, [id, onClose]);
    useEffect(() => { if (id) load(); }, [id, load]);
    const act = async (kind) => {
        setBusy(true);
        try {
            const r = await apiClient.post(`${API}/${id}/${kind}`, {});
            if (ok(r)) { message.success(kind === 'suspend' ? '已暂停' : '已恢复'); load(); onChanged(); }
        } catch (e) { err(e); } finally { setBusy(false); }
    };
    return (
        <Drawer open={!!id} onClose={onClose} width={760} title={d ? `${d.name} (#${d.id})` : '站点详情'}
            extra={d && (d.status === 'suspended'
                ? <Popconfirm title="恢复该站点?" onConfirm={() => act('unsuspend')}><Button type="primary" loading={busy}>恢复</Button></Popconfirm>
                : d.status !== 'deleted' && <Popconfirm title="暂停该站点?访客将看到暂停页。" okButtonProps={{ danger: true }} onConfirm={() => act('suspend')}><Button danger loading={busy}>暂停</Button></Popconfirm>)}>
            {!d ? null : (
                <Space direction="vertical" style={{ width: '100%' }} size="large">
                    <Descriptions column={2} size="small" bordered>
                        <Descriptions.Item label="状态"><StatusTag s={d.status} />{d.suspend_reason && <Text type="secondary"> ({d.suspend_reason === 'billing' ? '欠费' : '管理员'})</Text>}</Descriptions.Item>
                        <Descriptions.Item label="用户">#{d.user_id} {d.user_email}</Descriptions.Item>
                        <Descriptions.Item label="地址" span={2}>{d.url ? <a href={d.url} target="_blank" rel="noopener noreferrer">{d.url}</a> : '-'}</Descriptions.Item>
                        <Descriptions.Item label="网关 ID">{d.gateway_id || '-'}</Descriptions.Item>
                        <Descriptions.Item label="套餐">{d.subscription_id ? `订阅 #${d.subscription_id}` : '免费版'}</Descriptions.Item>
                        <Descriptions.Item label="发布版本">{d.published_version ? `v${d.published_version}` : '未发布'} {d.published_at && <Text type="secondary">{fmt(d.published_at)}</Text>}</Descriptions.Item>
                        {d.storage && <Descriptions.Item label="存储">{storageText(d.storage.used, d.storage.limit)}{d.storage.site_used !== undefined && <Text type="secondary"> (本站 {bytes(d.storage.site_used)})</Text>}</Descriptions.Item>}
                        <Descriptions.Item label="表单提交">{d.form_submissions ?? 0}</Descriptions.Item>
                        <Descriptions.Item label="默认语言">{d.default_lang}</Descriptions.Item>
                        <Descriptions.Item label="创建">{fmt(d.created_at)}</Descriptions.Item>
                        {d.plan && (
                            <Descriptions.Item label="套餐功能" span={2}>
                                <Space size={[4, 4]} wrap>
                                    {Object.entries(d.plan).map(([k, v]) => {
                                        const name = { sites: '站点', products: '商品', custom_domains: '自定义域名', staff: '员工', remove_badge: '去标识', transaction_fee_bps: '抽成(基点)', storage_mb: '存储(MB)' }[k] || k;
                                        const val = typeof v === 'boolean' ? (v ? '是' : '否') : Number(v) < 0 ? '不限' : String(v);
                                        return <Tag key={k} style={{ marginInlineEnd: 0 }}>{name}: {val}</Tag>;
                                    })}
                                </Space>
                            </Descriptions.Item>
                        )}
                    </Descriptions>
                    <Card size="small" title={`域名 (${listOf(d.domains).length})`}>
                        <Table size="small" rowKey="id" pagination={false} dataSource={listOf(d.domains)} columns={[
                            { title: '主机名', dataIndex: 'host', render: (v, r) => <Space>{v}{r.is_primary && <Tag color="blue">主域名</Tag>}</Space> },
                            { title: '类型', dataIndex: 'kind', render: (v) => (v === 'custom' ? '自定义' : '子域名') },
                            { title: '状态', dataIndex: 'status', render: (v, r) => { const [c, l] = DOMAIN_STATUS[v] || ['default', v]; return <Space direction="vertical" size={0}><Tag color={c}>{l}</Tag>{r.error && <Text type="danger" style={{ fontSize: 12 }}>{r.error}</Text>}</Space>; } },
                            { title: 'CNAME', dataIndex: 'cname_target', render: (v) => (v ? <Text code style={{ fontSize: 12 }}>{v}</Text> : '-') },
                        ]} />
                    </Card>
                    <Card size="small" title={`页面 (${listOf(d.pages).length})`}>
                        <Table size="small" rowKey="id" pagination={false} dataSource={listOf(d.pages)} columns={[
                            { title: '路径', dataIndex: 'path', render: (v) => <Text code>{v}</Text> },
                            { title: '类型', dataIndex: 'type' },
                            { title: '标题', dataIndex: 'title' },
                            { title: '更新', dataIndex: 'updated_at', render: fmt },
                        ]} />
                    </Card>
                    <Card size="small" title={`版本 (${listOf(d.versions).length})`}>
                        <Table size="small" rowKey="version" pagination={false} dataSource={listOf(d.versions)} columns={[
                            { title: '版本', dataIndex: 'version', render: (v, r) => <Space>v{v}{r.active && <Tag color="green">当前</Tag>}</Space> },
                            { title: '备注', dataIndex: 'note' },
                            { title: '发布时间', dataIndex: 'created_at', render: fmt },
                        ]} />
                    </Card>
                </Space>
            )}
        </Drawer>
    );
};

const Sites = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [f, setF] = useState({ q: '', status: '', user_id: '', page: 1, per_page: 50 });
    const [q, setQ] = useState('');
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const load = useCallback(() => {
        setLoading(true);
        apiClient.get(API, { params: Object.fromEntries(Object.entries(f).filter(([, v]) => v !== '')) })
            .then((r) => { const d = data(r); setRows(listOf(d)); setTotal(d?.total ?? listOf(d).length); })
            .catch((e) => err(e, '加载失败')).finally(() => setLoading(false));
    }, [f]);
    useEffect(() => { load(); }, [load]);
    const open = (sid) => navigate(adminPath(`/sites/list/${sid}`));
    const close = useCallback(() => navigate(adminPath('/sites/list')), [navigate]);
    return (
        <>
            <Space wrap style={{ marginBottom: 12 }}>
                <Input.Search allowClear placeholder="站点名 / 子域名 / 域名 / 用户邮箱" value={q} onChange={(e) => setQ(e.target.value)} onSearch={(v) => setF((x) => ({ ...x, q: v, page: 1 }))} style={{ width: 300 }} />
                <Select style={{ width: 130 }} value={f.status} onChange={(v) => setF((x) => ({ ...x, status: v, page: 1 }))}
                    options={[{ value: '', label: '全部状态' }, ...Object.entries(STATUS).map(([k, [, l]]) => ({ value: k, label: l }))]} />
                <InputNumber placeholder="用户 ID" value={f.user_id || null} onChange={(v) => setF((x) => ({ ...x, user_id: v || '', page: 1 }))} style={{ width: 120 }} />
                <Button icon={<ReloadOutlined />} onClick={load}>刷新</Button>
            </Space>
            <Table rowKey="id" loading={loading} dataSource={rows} scroll={{ x: 1400 }}
                pagination={{ current: f.page, pageSize: f.per_page, total, showSizeChanger: true, onChange: (page, per) => setF((x) => ({ ...x, page, per_page: per })) }}
                columns={[
                    { title: 'ID', dataIndex: 'id', width: 70 },
                    { title: '站点', key: 'n', width: 240, render: (_, r) => <Space direction="vertical" size={0}><a onClick={() => open(r.id)}>{r.name}</a>{r.url && <a href={r.url} target="_blank" rel="noopener noreferrer"><Text type="secondary" style={{ fontSize: 12 }}>{r.url.replace(/^https?:\/\//, '')}</Text></a>}</Space> },
                    { title: '用户', key: 'u', width: 220, render: (_, r) => <Space direction="vertical" size={0}><Text>#{r.user_id}</Text><Text type="secondary" style={{ fontSize: 12 }}>{r.user_email}</Text></Space> },
                    { title: '状态', dataIndex: 'status', width: 110, render: (v, r) => <Space size={2}><StatusTag s={v} />{r.suspend_reason === 'billing' && <Tag>欠费</Tag>}</Space> },
                    { title: '套餐', dataIndex: 'subscription_id', width: 110, render: (v) => (v ? `订阅 #${v}` : '免费版') },
                    { title: '存储', key: 'st', width: 150, render: (_, r) => (r.storage_bytes !== undefined ? storageText(r.storage_bytes, r.storage_limit) : '-') },
                    { title: '版本', dataIndex: 'published_version', width: 80, render: (v) => (v ? `v${v}` : '-') },
                    { title: '发布时间', dataIndex: 'published_at', width: 170, render: fmt },
                    { title: '创建', dataIndex: 'created_at', width: 170, render: fmt },
                    { title: '', key: 'x', width: 70, render: (_, r) => <a onClick={() => open(r.id)}>详情</a> },
                ]} />
            <SiteDetail id={id ? Number(id) : null} onClose={close} onChanged={load} />
        </>
    );
};

const Components = () => {
    const [f, setF] = useState({ q: '', category: '', context: '', page: 1, per_page: 100 });
    const [q, setQ] = useState('');
    const [d, setD] = useState({ items: [], total: 0 });
    const [cats, setCats] = useState([]);
    const [loading, setLoading] = useState(false);
    const [syncing, setSyncing] = useState(false);
    const [view, setView] = useState(null);
    const load = useCallback(() => {
        setLoading(true);
        apiClient.get(`${API}/components`, { params: Object.fromEntries(Object.entries(f).filter(([, v]) => v !== '')) })
            .then((r) => { const x = data(r); setD({ items: listOf(x), total: x?.total ?? 0, deprecated: x?.deprecated }); })
            .catch((e) => err(e, '加载失败')).finally(() => setLoading(false));
    }, [f]);
    useEffect(() => { load(); }, [load]);
    useEffect(() => {
        // 分类表来自用户侧同一张 sb_component_categories;后台没有单独的分类接口时从列表里汇总
        setCats((c) => [...new Set([...c, ...d.items.map((x) => x.category).filter(Boolean)])].sort());
    }, [d.items]);
    const sync = async () => {
        setSyncing(true);
        try {
            const r = await apiClient.post(`${API}/components/sync`, {}, { timeout: 180000 });
            const x = data(r) || {};
            Modal.info({
                title: '同步完成', width: 560,
                content: (
                    <Space direction="vertical" style={{ width: '100%' }}>
                        <Text>新增/更新:{listOf(x.added).length ? listOf(x.added).map((a) => (typeof a === 'string' ? a : `${a.name}@${a.version}`)).join(', ') : '无'}</Text>
                        <Text>未变化:{x.unchanged ?? 0}</Text>
                        {listOf(x.deprecated).length > 0 && <Text>已废弃:{listOf(x.deprecated).join(', ')}</Text>}
                        {listOf(x.invalid).length > 0 && (
                            <Alert type="error" message={`校验失败 ${listOf(x.invalid).length} 个`} description={<div style={{ maxHeight: 240, overflow: 'auto' }}>{listOf(x.invalid).map((iv, i) => <div key={i}><Text code>{iv.name || iv.file}</Text> {typeof iv === 'string' ? iv : (listOf(iv.problems).map((p) => `${p.path}: ${p.message}`).join('; ') || iv.error)}</div>)}</div>} />
                        )}
                    </Space>
                ),
            });
            load();
        } catch (e) { err(e, '同步失败'); } finally { setSyncing(false); }
    };
    const openView = async (c) => {
        setView({ ...c, loading: true });
        try {
            const r = await apiClient.get(`${API}/components`, { params: { q: c.name, full: 1, per_page: 20 } });
            const full = listOf(data(r)).find((x) => x.name === c.name);
            setView({ ...c, ...(full || {}), loading: false });
        } catch (e) { err(e); setView({ ...c, loading: false }); }
    };
    return (
        <>
            <Space wrap style={{ marginBottom: 12 }}>
                <Input.Search allowClear placeholder="名称 / 标签 / 描述" value={q} onChange={(e) => setQ(e.target.value)} onSearch={(v) => setF((x) => ({ ...x, q: v, page: 1 }))} style={{ width: 260 }} />
                <Select style={{ width: 160 }} value={f.category} onChange={(v) => setF((x) => ({ ...x, category: v, page: 1 }))} options={[{ value: '', label: '全部分类' }, ...cats.map((c) => ({ value: c, label: c }))]} />
                <Select style={{ width: 150 }} value={f.context} onChange={(v) => setF((x) => ({ ...x, context: v, page: 1 }))}
                    options={[{ value: '', label: '全部数据上下文' }, { value: 'none', label: '无(普通区块)' }, ...['shop', 'product', 'collection', 'post', 'blog', 'search'].map((c) => ({ value: c, label: c }))]} />
                <Button icon={<ReloadOutlined />} onClick={load}>刷新</Button>
                <Popconfirm title="从 sitebuilder/components/*.json 同步到平台组件库?" onConfirm={sync}>
                    <Button type="primary" icon={<SyncOutlined />} loading={syncing}>同步组件</Button>
                </Popconfirm>
                <Text type="secondary">共 {d.total} 个{d.deprecated ? `,已废弃 ${d.deprecated}` : ''}</Text>
            </Space>
            <Table rowKey={(r) => `${r.scope}:${r.name}`} loading={loading} dataSource={d.items} scroll={{ x: 900 }}
                pagination={{ current: f.page, pageSize: f.per_page, total: d.total, onChange: (page, per) => setF((x) => ({ ...x, page, per_page: per })) }}
                columns={[
                    { title: '组件', key: 'n', render: (_, r) => <Space direction="vertical" size={0}><a onClick={() => openView(r)}>{r.label || r.name}</a><Text type="secondary" style={{ fontSize: 12 }}>{r.name}@{r.version}</Text></Space> },
                    { title: '分类', dataIndex: 'category', width: 120 },
                    { title: '范围', dataIndex: 'scope', width: 90, render: (v) => (v === 'platform' ? <Tag color="blue">平台</Tag> : <Tag>账户</Tag>) },
                    { title: '数据上下文', dataIndex: 'context', width: 140, render: (v) => listOf(v).map((x) => <Tag key={x}>{x}</Tag>) },
                    { title: '标签', dataIndex: 'tags', render: (v) => listOf(v).slice(0, 6).map((x) => <Tag key={x} style={{ fontSize: 11 }}>{x}</Tag>) },
                    { title: '状态', dataIndex: 'status', width: 80, render: (v) => (v === 'deprecated' ? <Tag>废弃</Tag> : <Tag color="green">启用</Tag>) },
                    { title: '更新', dataIndex: 'created_at', width: 170, render: fmt },
                ]} />
            <Drawer open={!!view} onClose={() => setView(null)} width={760} title={view ? `${view.label || view.name} · ${view.name}@${view.version}` : ''}>
                {view && (
                    <Space direction="vertical" style={{ width: '100%' }}>
                        {view.description && <Text>{view.description}</Text>}
                        {view.thumbnail && <img src={view.thumbnail} alt="" style={{ maxWidth: '100%', border: '1px solid #eee' }} />}
                        {view.loading ? <Text type="secondary">加载定义…</Text> : view.definition
                            ? <pre style={{ fontSize: 12, background: '#f6f8fa', padding: 12, borderRadius: 6, maxHeight: '70vh', overflow: 'auto' }}>{JSON.stringify(view.definition, null, 2)}</pre>
                            : <Empty description="没有定义" />}
                    </Space>
                )}
            </Drawer>
        </>
    );
};

const ModeKeys = ({ mode, value, onChange }) => {
    const v = value || {};
    const set = (k, x) => onChange({ ...v, [k]: x });
    return (
        <Card size="small" title={<Space>{mode === 'test' ? <Tag color="orange">测试模式</Tag> : <Tag color="green">正式模式</Tag>}{v.key_source && <Text type="secondary" style={{ fontSize: 12 }}>密钥来源:{{ settings: '本页设置', env: '.env STRIPE_SECRET_KEY', none: '未配置' }[v.key_source] || v.key_source}</Text>}</Space>} style={{ marginBottom: 12 }}>
            <Form layout="vertical">
                <Form.Item label="Publishable key"><Input value={v.publishable_key} placeholder={`pk_${mode}_…`} onChange={(e) => set('publishable_key', e.target.value)} /></Form.Item>
                <Form.Item label="Secret key" extra="显示的是掩码;留空/不改则保持原值,填 - 清除"><Input.Password value={v.secret_key} placeholder={`sk_${mode}_… 或 rk_${mode}_…`} onChange={(e) => set('secret_key', e.target.value)} visibilityToggle={false} /></Form.Item>
                <Form.Item label="Webhook signing secret" style={{ marginBottom: 0 }}><Input.Password value={v.webhook_secret} placeholder="whsec_…" onChange={(e) => set('webhook_secret', e.target.value)} visibilityToggle={false} /></Form.Item>
            </Form>
        </Card>
    );
};

const Payments = () => {
    const navigate = useNavigate();
    const [s, setS] = useState(null);
    const [orig, setOrig] = useState(null);
    const [missing, setMissing] = useState(false);
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(false);
    const [accounts, setAccounts] = useState([]);
    const [accLoading, setAccLoading] = useState(false);
    const load = useCallback(() => {
        apiClient.get(`${API}/payments/settings`, { skipGlobalErrorToast: true })
            .then((r) => { const d = data(r); setS(d); setOrig(d); })
            .catch((e) => { if (e?.response?.status === 404) setMissing(true); else err(e, '加载失败'); });
        setAccLoading(true);
        apiClient.get(`${API}/payments/accounts`, { skipGlobalErrorToast: true }).then((r) => setAccounts(listOf(data(r)))).catch(() => {}).finally(() => setAccLoading(false));
    }, []);
    useEffect(() => { load(); }, [load]);
    if (missing) return <Result status="info" title="收款设置接口尚未上线" subTitle="/api/admin/sites/payments/settings 返回 404" />;
    if (!s) return null;
    const save = async () => {
        // 只提交改过的密钥(掩码原样提交服务端也会忽略)
        const body = { enabled: s.enabled, mode: s.mode, application_fee_bps: s.application_fee_bps, countries: s.countries, account_type: s.account_type };
        ['test', 'live'].forEach((m) => {
            const a = s[m] || {}; const o = orig?.[m] || {};
            const out = {};
            ['publishable_key', 'secret_key', 'webhook_secret'].forEach((k) => { if ((a[k] ?? '') !== (o[k] ?? '')) out[k] = a[k] ?? ''; });
            if (Object.keys(out).length) body[m] = out;
        });
        setSaving(true);
        try {
            const r = await apiClient.put(`${API}/payments/settings`, body);
            const d = data(r); setS(d); setOrig(d); message.success('已保存');
        } catch (e) { err(e, '保存失败'); } finally { setSaving(false); }
    };
    const test = async () => {
        setTesting(true);
        try {
            const x = data(await apiClient.post(`${API}/payments/test`, {}, { timeout: 60000 })) || {};
            Modal[x.balance_ok && x.connect_enabled ? 'success' : 'warning']({
                title: '连接测试',
                content: (
                    <Descriptions column={1} size="small">
                        <Descriptions.Item label="模式">{x.mode}{x.livemode !== undefined && ` (livemode=${String(x.livemode)})`}</Descriptions.Item>
                        <Descriptions.Item label="密钥来源">{x.key_source}</Descriptions.Item>
                        <Descriptions.Item label="余额接口">{x.balance_ok ? <Tag color="green">正常</Tag> : <Text type="danger">{x.balance_error || '失败'}</Text>}</Descriptions.Item>
                        <Descriptions.Item label="Connect">{x.connect_enabled ? <Tag color="green">已开通 · 样本 {x.connected_accounts_sample}</Tag> : <Text type="danger">{x.connect_error || '未开通'}</Text>}</Descriptions.Item>
                    </Descriptions>
                ),
            });
        } catch (e) { err(e, '测试失败'); } finally { setTesting(false); }
    };
    return (
        <Space direction="vertical" style={{ width: '100%' }} size="large">
            <Card title="Stripe Connect 平台设置" extra={<Space><Button onClick={test} loading={testing}>测试连接</Button><Button type="primary" onClick={save} loading={saving}>保存</Button></Space>}>
                <Form layout="vertical" style={{ maxWidth: 820 }}>
                    <Space size="large" wrap>
                        <Form.Item label="启用店铺收款"><Switch checked={!!s.enabled} onChange={(v) => setS({ ...s, enabled: v })} /></Form.Item>
                        <Form.Item label="当前模式"><Radio.Group value={s.mode} onChange={(e) => setS({ ...s, mode: e.target.value })} optionType="button" options={[{ value: 'test', label: '测试' }, { value: 'live', label: '正式' }]} /></Form.Item>
                        <Form.Item label="平台抽成(基点,100 = 1%)"><InputNumber min={0} max={10000} value={s.application_fee_bps} onChange={(v) => setS({ ...s, application_fee_bps: v || 0 })} addonAfter={`${((s.application_fee_bps || 0) / 100).toFixed(2)}%`} /></Form.Item>
                        <Form.Item label="账户类型"><Select style={{ width: 140 }} value={s.account_type} onChange={(v) => setS({ ...s, account_type: v })} options={[{ value: 'express', label: 'Express' }, { value: 'standard', label: 'Standard' }]} /></Form.Item>
                    </Space>
                    <Form.Item label="开户国家(ISO 两位代码)">
                        <Select mode="tags" value={s.countries || []} onChange={(v) => setS({ ...s, countries: v.map((x) => String(x).toUpperCase().slice(0, 2)) })} style={{ width: '100%' }} />
                    </Form.Item>
                    {s.webhook_url && (
                        <Alert type="info" style={{ marginBottom: 12 }} message={<span>Webhook 地址(Connect 事件,两种模式各建一个):<Text copyable code>{s.webhook_url}</Text></span>}
                            description={listOf(s.webhook_events).length ? <span>事件:{listOf(s.webhook_events).join(', ')}</span> : null} />
                    )}
                </Form>
                <ModeKeys mode="test" value={s.test} onChange={(v) => setS({ ...s, test: v })} />
                <ModeKeys mode="live" value={s.live} onChange={(v) => setS({ ...s, live: v })} />
            </Card>
            <Card title="商家收款账户" extra={<Button icon={<ReloadOutlined />} onClick={load}>刷新</Button>}>
                <Table rowKey={(r) => r.id || `${r.site_id}-${r.mode}`} size="small" loading={accLoading} dataSource={accounts} scroll={{ x: 900 }} columns={[
                    { title: '站点', key: 's', render: (_, r) => <Space direction="vertical" size={0}><a onClick={() => navigate(adminPath(`/sites/list/${r.site_id}`))}>{r.site_name || `#${r.site_id}`}</a>{r.user_email && <Text type="secondary" style={{ fontSize: 12 }}>{r.user_email}</Text>}</Space> },
                    { title: '账户', dataIndex: 'account_id', render: (v) => (v ? <Text copyable code>{v}</Text> : '-') },
                    { title: '模式', dataIndex: 'mode', width: 80, render: (v) => (v === 'test' ? <Tag color="orange">测试</Tag> : <Tag color="green">正式</Tag>) },
                    { title: '资料', dataIndex: 'details_submitted', width: 70, render: (v) => (v ? <Tag color="green">✓</Tag> : <Tag>✗</Tag>) },
                    { title: '收款', dataIndex: 'charges_enabled', width: 70, render: (v) => (v ? <Tag color="green">✓</Tag> : <Tag>✗</Tag>) },
                    { title: '提现', dataIndex: 'payouts_enabled', width: 70, render: (v) => (v ? <Tag color="green">✓</Tag> : <Tag>✗</Tag>) },
                    { title: '错误', dataIndex: 'last_error', render: (v) => (v ? <Text type="danger" style={{ fontSize: 12 }}>{v}</Text> : '-') },
                    { title: '更新', dataIndex: 'updated_at', width: 170, render: fmt },
                ]} />
            </Card>
        </Space>
    );
};

const TABS = [
    { key: 'list', label: '站点', C: Sites },
    { key: 'components', label: '组件库', C: Components },
    { key: 'payments', label: '收款设置', C: Payments },
];

const SitesAdmin = () => {
    const { tab = 'list' } = useParams();
    const navigate = useNavigate();
    const active = TABS.find((x) => x.key === tab) ? tab : 'list';
    return (
        <Card title="建站">
            <Tabs activeKey={active} onChange={(k) => navigate(adminPath(`/sites/${k}`))} destroyInactiveTabPane
                items={TABS.map(({ key, label, C }) => ({ key, label, children: <C /> }))} />
        </Card>
    );
};

export default SitesAdmin;
